import { BodyRow } from 'app/components/ChakraTableList/types';
import { MutableRefObject, useEffect, useReducer } from 'react';

interface ReducerState {
  rows: BodyRow[];
  toggle: Record<string, boolean>;
}

function toggleSubRow(
  { toggle, rows }: ReducerState,
  id: string
): ReducerState {
  const rowIndex = rows.findIndex((r) => r.id === id);
  const row = rows[rowIndex];
  const { children } = row;
  if (!children) {
    return {
      rows,
      toggle,
    };
  }

  const toggleUpdated = {
    ...toggle,
  };

  const isExpanded = toggleUpdated[row.id as string];
  const newRows = [...rows];
  toggleUpdated[row.id as string] = !isExpanded;
  if (isExpanded) {
    newRows.splice(rowIndex + 1, children.length);
  } else {
    newRows.splice(rowIndex + 1, 0, ...children);
  }
  return {
    rows: newRows,
    toggle: toggleUpdated,
  };
}

const setState: (state: ReducerState, rows: BodyRow[]) => ReducerState = (
  state,
  rows
) => {
  const hasSubRows = rows.find((row) => row.children);
  if (!hasSubRows) {
    return {
      rows,
      toggle: {},
    };
  }
  const toggleUpdated = {
    ...state.toggle,
  };
  const rowsUpdated: BodyRow[] = rows
    .map((row) => {
      const toggled = state.toggle[row.id as string];
      if (toggled || toggled === undefined) {
        toggleUpdated[row.id as string] = true;
        return [row, ...(row.children || [])];
      }
      return row;
    })
    .flat();

  return {
    rows: rowsUpdated,
    toggle: toggleUpdated,
  };
};

const reducer = (
  state: ReducerState,
  action: { type: string; payload: unknown }
): ReducerState => {
  switch (action.type) {
    case 'setRows': {
      return setState(state, action.payload as BodyRow[]);
    }
    case 'toggle': {
      return toggleSubRow(state, action.payload as string);
    }
    default:
      return state;
  }
};

export const useRows = (
  rows: BodyRow[],
  toggledRows: MutableRefObject<Record<string, boolean>>
) => {
  const [state, localDispatch] = useReducer(reducer, { rows, toggle: {} });

  useEffect(() => {
    localDispatch({ type: 'setRows', payload: rows });
  }, [rows]);

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    toggledRows.current = state.toggle;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.toggle]);

  return {
    rows: state.rows,
    toggleRow: (rowId: string) => {
      localDispatch({ type: 'toggle', payload: rowId });
    },
  };
};
